import React, { useState } from "react";
import { supabase } from "./supabase";

// Phase B — tenant self-service Settings.
//
// Lets a tenant admin edit their own industries (with sub-industries)
// and pricing model. Persists the whole `config` jsonb via the
// update_tenant_config() RPC, which authorizes tenant admins and
// platform admins server-side.

const uid = () => Math.random().toString(36).slice(2, 9);

const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, marginBottom: 16 };
const inp = { padding: "8px 11px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" };
const lbl = { fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };
const sectionTitle = { fontSize: 15, fontWeight: 800, color: "#1B3A5C", marginBottom: 4 };
const sectionHint = { fontSize: 12, color: "#94A3B8", marginBottom: 12 };

function MiniBtn({ onClick, children, danger }) {
  return (
    <button onClick={onClick} style={{
      padding: "5px 10px", fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: "pointer",
      border: `1px solid ${danger ? "#FCA5A5" : "#CBD5E1"}`,
      background: danger ? "#FEF2F2" : "#fff", color: danger ? "#991B1B" : "#475569",
    }}>{children}</button>
  );
}

export default function TenantSettings({ tenant, tenantConfig, onClose, onSaved }) {
  const [tab, setTab] = useState("industries");
  const [cfg, setCfg] = useState(() => JSON.parse(JSON.stringify({
    industries: tenantConfig.industries || [],
    pricing: tenantConfig.pricing || { model: "tiers", tiers: [], packages: [], packageDiscount: 0.1, services: [], programs: [], programAddons: [], discounts: [] },
  })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pricing = cfg.pricing;
  const setPricing = (patch) => setCfg(c => ({ ...c, pricing: { ...c.pricing, ...patch } }));
  // Edit one element of a pricing array by index.
  const setRow = (key, i, patch) => setPricing({ [key]: pricing[key].map((r, ri) => ri === i ? { ...r, ...patch } : r) });
  const addRow = (key, row) => setPricing({ [key]: [...(pricing[key] || []), { id: uid(), ...row }] });
  const delRow = (key, i) => setPricing({ [key]: pricing[key].filter((_, ri) => ri !== i) });

  const save = async () => {
    setSaving(true);
    setError(null);
    // Drop blank industry/item names so the form stays tidy.
    const clean = {
      industries: cfg.industries
        .filter(i => (i.name || "").trim())
        .map(i => ({ name: i.name.trim(), subs: (i.subs || []).map(s => s.trim()).filter(Boolean) })),
      pricing: cfg.pricing,
    };
    try {
      const { error: rpcErr } = await supabase.rpc("update_tenant_config", {
        p_tenant_id: tenant.id,
        p_config: clean,
      });
      if (rpcErr) throw rpcErr;
      await onSaved(clean);
      onClose();
    } catch (e) {
      setError(e.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div className="modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 760, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div className="modal-title">Workspace Settings — {tenant.name}</div>
          <button className="btn btn-ghost" style={{ padding: "4px 8px" }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>
          Industries and pricing here apply only to this workspace.
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[["industries", "Industries"], ["pricing", "Pricing"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, cursor: "pointer",
              border: `1.5px solid ${tab === k ? "#2563EB" : "#E2E8F0"}`,
              background: tab === k ? "#EFF6FF" : "#fff", color: tab === k ? "#1E40AF" : "#64748B",
            }}>{label}</button>
          ))}
        </div>

        <div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
          {tab === "industries" ? (
            <IndustriesEditor cfg={cfg} setCfg={setCfg} />
          ) : (
            <PricingEditor pricing={pricing} setPricing={setPricing} setRow={setRow} addRow={addRow} delRow={delRow} />
          )}
        </div>

        {error && (
          <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16, paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function IndustriesEditor({ cfg, setCfg }) {
  const setInd = (i, patch) => setCfg(c => ({ ...c, industries: c.industries.map((r, ri) => ri === i ? { ...r, ...patch } : r) }));
  const addInd = () => setCfg(c => ({ ...c, industries: [...c.industries, { name: "", subs: [] }] }));
  const delInd = (i) => setCfg(c => ({ ...c, industries: c.industries.filter((_, ri) => ri !== i) }));

  return (
    <div>
      <div style={sectionTitle}>Industries</div>
      <div style={sectionHint}>The industries this workspace prospects against, each with its own sub-industries. These drive the lead form dropdowns.</div>
      {cfg.industries.map((ind, i) => (
        <div key={i} style={card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <input style={{ ...inp, flex: 1, fontWeight: 700 }} value={ind.name}
              onChange={e => setInd(i, { name: e.target.value })} placeholder="Industry name" />
            <MiniBtn danger onClick={() => delInd(i)}>Remove</MiniBtn>
          </div>
          <div style={lbl}>Sub-industries</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(ind.subs || []).map((s, si) => (
              <span key={si} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#F1F5F9", borderRadius: 7, padding: "2px 4px 2px 8px" }}>
                <input style={{ ...inp, border: "none", background: "transparent", padding: "2px 0", width: Math.max(60, s.length * 7 + 12) }}
                  value={s} onChange={e => setInd(i, { subs: ind.subs.map((x, xi) => xi === si ? e.target.value : x) })} />
                <button onClick={() => setInd(i, { subs: ind.subs.filter((_, xi) => xi !== si) })}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94A3B8", fontSize: 14 }}>✕</button>
              </span>
            ))}
            <MiniBtn onClick={() => setInd(i, { subs: [...(ind.subs || []), ""] })}>+ Sub-industry</MiniBtn>
          </div>
        </div>
      ))}
      <MiniBtn onClick={addInd}>+ Add industry</MiniBtn>
    </div>
  );
}

function PricingEditor({ pricing, setPricing, setRow, addRow, delRow }) {
  const isPrograms = pricing.model === "programs";
  return (
    <div>
      <div style={sectionTitle}>Pricing model</div>
      <div style={sectionHint}>How this workspace prices deals. Changing the model changes the lead form's pricing fields.</div>
      <div style={card}>
        <div style={lbl}>Model</div>
        <select style={{ ...inp, width: 280 }} value={pricing.model}
          onChange={e => setPricing({ model: e.target.value })}>
          <option value="tiers">Per-user tiers + modular packages</option>
          <option value="programs">Per-seat programs + discounts</option>
        </select>
        <div style={{ fontSize: 11, color: "#B45309", marginTop: 8 }}>
          ⚠ Switching the model changes which pricing fields appear on every lead. Existing leads keep their data.
        </div>
      </div>

      {!isPrograms ? (
        <>
          <RowSection title="Tiers" hint="Per-user/month plans. Each lead picks one tier (or Modular).">
            {pricing.tiers.map((t, i) => (
              <div key={t.id || i} style={rowFlex}>
                <input style={{ ...inp, flex: 2 }} value={t.name} placeholder="Tier name"
                  onChange={e => setRow("tiers", i, { name: e.target.value })} />
                <NumIn value={t.rate} onChange={v => setRow("tiers", i, { rate: v })} prefix="$" suffix="/user/mo" />
                <MiniBtn danger onClick={() => delRow("tiers", i)}>✕</MiniBtn>
              </div>
            ))}
            <MiniBtn onClick={() => addRow("tiers", { name: "", rate: 0 })}>+ Add tier</MiniBtn>
          </RowSection>

          <RowSection title="Modular packages" hint="À-la-carte packages priced per user/month.">
            {pricing.packages.map((p, i) => (
              <div key={p.id || i} style={{ ...card, padding: 14 }}>
                <div style={rowFlex}>
                  <input style={{ ...inp, flex: 2 }} value={p.name} placeholder="Package name"
                    onChange={e => setRow("packages", i, { name: e.target.value })} />
                  <NumIn value={p.price} onChange={v => setRow("packages", i, { price: v })} prefix="$" suffix="/user/mo" />
                  <MiniBtn danger onClick={() => delRow("packages", i)}>✕</MiniBtn>
                </div>
                <div style={{ ...lbl, marginTop: 8 }}>Includes (comma-separated)</div>
                <input style={{ ...inp, width: "100%" }} value={(p.includes || []).join(", ")}
                  onChange={e => setRow("packages", i, { includes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                  placeholder="Feature one, Feature two" />
              </div>
            ))}
            <MiniBtn onClick={() => addRow("packages", { name: "", price: 0, includes: [] })}>+ Add package</MiniBtn>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
              <span style={{ fontSize: 13, color: "#475569" }}>Discount when 2+ packages combined:</span>
              <NumIn value={Math.round((pricing.packageDiscount || 0) * 100)}
                onChange={v => setPricing({ packageDiscount: (Number(v) || 0) / 100 })} suffix="%" />
            </div>
          </RowSection>

          <RowSection title="Additional services" hint="Add-on services available to any lead.">
            {pricing.services.map((s, i) => (
              <div key={s.id || i} style={rowFlex}>
                <input style={{ ...inp, flex: 2 }} value={s.name} placeholder="Service name"
                  onChange={e => setRow("services", i, { name: e.target.value })} />
                <NumIn value={s.price} onChange={v => setRow("services", i, { price: v })} prefix="$" />
                <input style={{ ...inp, width: 130 }} value={s.unit || ""} placeholder="per year"
                  onChange={e => setRow("services", i, { unit: e.target.value })} />
                <label style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" checked={!!s.perUser} onChange={e => setRow("services", i, { perUser: e.target.checked })} />
                  per user
                </label>
                <MiniBtn danger onClick={() => delRow("services", i)}>✕</MiniBtn>
              </div>
            ))}
            <MiniBtn onClick={() => addRow("services", { name: "", price: 0, unit: "per year", perUser: false })}>+ Add service</MiniBtn>
          </RowSection>
        </>
      ) : (
        <>
          <RowSection title="Programs" hint="Flat per-seat programs. Group lets the lead form cluster them.">
            {pricing.programs.map((p, i) => (
              <div key={p.id || i} style={{ ...card, padding: 14 }}>
                <div style={rowFlex}>
                  <input style={{ ...inp, flex: 2 }} value={p.name} placeholder="Program name"
                    onChange={e => setRow("programs", i, { name: e.target.value })} />
                  <MiniBtn danger onClick={() => delRow("programs", i)}>✕</MiniBtn>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                  <Field label="Monthly"><NumIn value={p.monthly} onChange={v => setRow("programs", i, { monthly: v })} prefix="$" /></Field>
                  <Field label="Annual"><NumIn value={p.annual} onChange={v => setRow("programs", i, { annual: v })} prefix="$" /></Field>
                  <Field label="Group">
                    <input style={{ ...inp, width: 150 }} value={p.group || ""} placeholder="e.g. prep"
                      onChange={e => setRow("programs", i, { group: e.target.value })} />
                  </Field>
                </div>
              </div>
            ))}
            <MiniBtn onClick={() => addRow("programs", { name: "", monthly: 0, annual: 0, group: "" })}>+ Add program</MiniBtn>
          </RowSection>

          <RowSection title="Program add-ons" hint="Optional per-seat add-ons.">
            {pricing.programAddons.map((a, i) => (
              <div key={a.id || i} style={rowFlex}>
                <input style={{ ...inp, flex: 2 }} value={a.name} placeholder="Add-on name"
                  onChange={e => setRow("programAddons", i, { name: e.target.value })} />
                <NumIn value={a.price} onChange={v => setRow("programAddons", i, { price: v })} prefix="$" />
                <input style={{ ...inp, width: 150 }} value={a.unit || ""} placeholder="per student/mo"
                  onChange={e => setRow("programAddons", i, { unit: e.target.value })} />
                <MiniBtn danger onClick={() => delRow("programAddons", i)}>✕</MiniBtn>
              </div>
            ))}
            <MiniBtn onClick={() => addRow("programAddons", { name: "", price: 0, unit: "per student/mo" })}>+ Add add-on</MiniBtn>
          </RowSection>

          <RowSection title="Discounts" hint="Selectable on a lead. Military / First Responder / Multi-Student keep their special math by id.">
            {pricing.discounts.map((d, i) => (
              <div key={d.id || i} style={{ ...card, padding: 14 }}>
                <div style={rowFlex}>
                  <input style={{ ...inp, flex: 2 }} value={d.name} placeholder="Discount name"
                    onChange={e => setRow("discounts", i, { name: e.target.value })} />
                  <MiniBtn danger onClick={() => delRow("discounts", i)}>✕</MiniBtn>
                </div>
                <input style={{ ...inp, width: "100%", marginTop: 8 }} value={d.desc || ""} placeholder="Description"
                  onChange={e => setRow("discounts", i, { desc: e.target.value })} />
              </div>
            ))}
            <MiniBtn onClick={() => addRow("discounts", { name: "", desc: "" })}>+ Add discount</MiniBtn>
          </RowSection>
        </>
      )}
    </div>
  );
}

const rowFlex = { display: "flex", gap: 8, alignItems: "center", marginBottom: 8 };

function RowSection({ title, hint, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={sectionTitle}>{title}</div>
      <div style={sectionHint}>{hint}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={lbl}>{label}</div>
      {children}
    </div>
  );
}

function NumIn({ value, onChange, prefix, suffix }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {prefix && <span style={{ fontSize: 12, color: "#94A3B8" }}>{prefix}</span>}
      <input type="number" min="0" style={{ ...inp, width: 80 }} value={value ?? 0}
        onChange={e => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
      {suffix && <span style={{ fontSize: 11, color: "#94A3B8" }}>{suffix}</span>}
    </span>
  );
}
